import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/api/client';
import { FieldError } from './form-utils';
import { errorClass, useFieldErrors } from './use-field-errors';

export default function BoxForm({ box, onClose }) {
  const queryClient = useQueryClient();
  const { fieldErrors, handleError, clearErrors } = useFieldErrors();

  const [formData, setFormData] = useState({
    name: box?.name ?? '',
    length: box?.length ?? '',
    width: box?.width ?? '',
    height: box?.height ?? '',
  });

  const saveMutation = useMutation({
    mutationFn: (data) =>
      box ? api.entities.ShippingBox.update(box.id, data) : api.entities.ShippingBox.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['boxes'] });
      toast.success(box ? 'Box updated' : 'Box added');
      onClose();
    },
    onError: (error) => handleError(error, 'Failed to save box'),
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    clearErrors();
    // Sent as typed; the API coerces and is the one place that decides what a
    // valid dimension is.
    saveMutation.mutate(formData);
  };

  const set = (key) => (event) => setFormData({ ...formData, [key]: event.target.value });

  return (
    <Card className="border-purple-200 shadow-md">
      <CardHeader className="bg-purple-50 border-b">
        <div className="flex justify-between items-center">
          <CardTitle className="text-lg">{box ? 'Edit box' : 'Add box'}</CardTitle>
          <Button type="button" variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="box-name">Box name</Label>
            <Input
              id="box-name"
              value={formData.name}
              onChange={set('name')}
              placeholder="e.g. Medium Box"
              className={errorClass(fieldErrors.name)}
              required
            />
            <FieldError message={fieldErrors.name} />
          </div>

          <div className="grid grid-cols-3 gap-4">
            {[
              ['length', 'Length (in)', '12'],
              ['width', 'Width (in)', '8'],
              ['height', 'Height (in)', '6'],
            ].map(([key, label, placeholder]) => (
              <div key={key} className="space-y-2">
                <Label htmlFor={`box-${key}`}>{label}</Label>
                <Input
                  id={`box-${key}`}
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData[key]}
                  onChange={set(key)}
                  placeholder={placeholder}
                  className={errorClass(fieldErrors[key])}
                  required
                />
                <FieldError message={fieldErrors[key]} />
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saveMutation.isPending}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Save className="w-4 h-4 mr-2" />
              {saveMutation.isPending ? 'Saving…' : 'Save box'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
